import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, MessageCircle, IndianRupee, StickyNote, Zap } from 'lucide-react';
import { api, inr, timeAgo, STATUS_STYLES } from '../api';
import { toast } from 'sonner';

const STATUSES = ['Open', 'In-Progress', 'High-Priority', 'Closed'];
const OUTCOMES = ['CONNECTED', 'NO_ANSWER', 'BUSY', 'WRONG_NUMBER', 'OTHER'];

export default function LeadModal({ lead, agent, onClose, refresh, sendActivity }) {
  const [note, setNote] = useState('');
  const [tpChannel, setTpChannel] = useState(null);
  const [tpOutcome, setTpOutcome] = useState('CONNECTED');
  const [tpNote, setTpNote] = useState('');
  const [convertAmount, setConvertAmount] = useState(lead.cart_value || '');
  const [showConvert, setShowConvert] = useState(false);
  const [quickRemarks, setQuickRemarks] = useState([]);

  useEffect(() => { api.get('/quick-remarks').then((r) => setQuickRemarks(r.data.items)).catch(() => {}); }, []);

  const addQuickRemark = async (text) => {
    await api.post(`/leads/${lead.id}/followups`, { note: text, by_name: agent.name });
    toast.success('Remark logged ⚡');
    sendActivity(`Remarked on ${lead.customer_name}: ${text}`);
    refresh();
  };

  const setStatus = async (status) => {
    await api.patch(`/leads/${lead.id}`, { status });
    toast.success(`Status → ${status}`);
    sendActivity(`Moved ${lead.customer_name} to ${status}`);
    refresh();
  };

  const addFollowup = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${lead.id}/followups`, { note, by_name: agent.name });
    toast.success('Follow-up note added');
    sendActivity(`Noted follow-up on ${lead.customer_name}`);
    setNote('');
    refresh();
  };

  const logTouchpoint = async () => {
    await api.post(`/leads/${lead.id}/touchpoints`, { channel: tpChannel, outcome: tpOutcome, note: tpNote, by_name: agent.name });
    toast.success(`${tpChannel === 'CALL' ? 'Call' : 'WhatsApp'} logged`);
    sendActivity(`${tpChannel === 'CALL' ? 'Called' : 'WhatsApped'} ${lead.customer_name}`);
    setTpChannel(null); setTpNote('');
    refresh();
  };

  const convert = async () => {
    await api.post(`/leads/${lead.id}/convert`, { amount: Number(convertAmount) || 0, by_name: agent.name });
    toast.success(`🎉 ${lead.customer_name} converted for ${inr(convertAmount)}!`);
    sendActivity(`Converted ${lead.customer_name} · ${inr(convertAmount)}`);
    setShowConvert(false);
    refresh();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="lead-modal-overlay">
      <div
        className="bg-[#FFFDF4] border-2 border-yellow-300 rounded-lg sticky-shadow w-full max-w-lg max-h-[88vh] overflow-y-auto scroll-thin relative rotate-[-0.3deg]"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-modal"
      >
        <div className="tape" />
        <div className="flex items-start justify-between p-4 pb-2 border-b border-dashed border-yellow-300">
          <div>
            <h3 className="font-display font-extrabold text-xl text-slate-900">{lead.customer_name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{lead.order_id} · {lead.mobile_number} · {lead.email}</p>
            <p className="text-xs text-slate-500">{lead.payment_method} · payment <b>{lead.payment_status}</b> · via {lead.source}</p>
          </div>
          <button data-testid="lead-modal-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-yellow-100 text-slate-500"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          {lead.products?.length > 0 && (
            <div className="bg-white/70 border border-yellow-200 rounded-lg p-2.5">
              {lead.products.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1">
                  {p.image ? (
                    <a href={p.image} target="_blank" rel="noreferrer" className="shrink-0">
                      <img src={p.image} alt={p.name} data-testid={`lead-product-image-${i}`}
                        className="w-12 h-12 rounded-lg object-cover border border-yellow-200 bg-white hover:scale-105 transition-transform" />
                    </a>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-yellow-50 border border-yellow-200 flex items-center justify-center text-yellow-400 text-lg shrink-0">🧸</div>
                  )}
                  <span className="text-slate-700 flex-1 min-w-0">{p.name} × {p.qty}</span>
                  <span className="font-bold text-slate-900 shrink-0">{inr(p.price * p.qty)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm pt-1.5 mt-1 border-t border-yellow-200 font-extrabold text-slate-900">
                <span>Cart Value</span><span>{inr(lead.cart_value)}</span>
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Status</p>
            <div className="flex gap-1.5 flex-wrap">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`lead-status-${s.toLowerCase()}`} onClick={() => setStatus(s)}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-all ${lead.status === s ? STATUS_STYLES[s] + ' ring-2 ring-offset-1 ring-slate-300' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {!lead.converted && (
            <div className="flex gap-2">
              <button data-testid="log-call-button" onClick={() => setTpChannel('CALL')}
                className="flex-1 flex items-center justify-center gap-1.5 bg-sky-600 text-white rounded-lg py-2 text-xs font-bold hover:bg-sky-700 transition-colors">
                <Phone size={13} /> Log Call
              </button>
              <button data-testid="log-whatsapp-button" onClick={() => setTpChannel('WHATSAPP')}
                className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 text-white rounded-lg py-2 text-xs font-bold hover:bg-emerald-700 transition-colors">
                <MessageCircle size={13} /> Log WhatsApp
              </button>
              <button data-testid="convert-lead-button" onClick={() => setShowConvert(!showConvert)}
                className="flex-1 flex items-center justify-center gap-1.5 bg-amber-500 text-white rounded-lg py-2 text-xs font-bold hover:bg-amber-600 transition-colors">
                <IndianRupee size={13} /> Convert
              </button>
            </div>
          )}
          {lead.converted && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-2.5 text-center">
              <p className="font-hand text-xl text-emerald-800 font-bold">🎉 Converted for {inr(lead.converted_amount)}</p>
              <p className="text-[10px] text-emerald-600">{timeAgo(lead.converted_at)}</p>
            </div>
          )}

          {tpChannel && (
            <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="touchpoint-form">
              <p className="text-xs font-bold text-slate-700">Log {tpChannel === 'CALL' ? 'Call' : 'WhatsApp'} outcome</p>
              <select data-testid="touchpoint-outcome" value={tpOutcome} onChange={(e) => setTpOutcome(e.target.value)}
                className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                {OUTCOMES.map((o) => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
              </select>
              <input data-testid="touchpoint-note" value={tpNote} onChange={(e) => setTpNote(e.target.value)} placeholder="Quick note (optional)"
                className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <div className="flex gap-2">
                <button data-testid="touchpoint-save" onClick={logTouchpoint} className="flex-1 bg-slate-900 text-white rounded py-1.5 text-xs font-bold hover:bg-slate-700">Save</button>
                <button onClick={() => setTpChannel(null)} className="px-3 text-xs text-slate-500 hover:text-slate-800">Cancel</button>
              </div>
            </div>
          )}

          {showConvert && !lead.converted && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex gap-2 items-center" data-testid="convert-form">
              <input data-testid="convert-amount-input" type="number" value={convertAmount} onChange={(e) => setConvertAmount(e.target.value)}
                placeholder="Amount ₹" className="flex-1 border border-amber-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500" />
              <button data-testid="convert-confirm-button" onClick={convert} className="bg-amber-500 text-white rounded px-4 py-1.5 text-xs font-bold hover:bg-amber-600">Confirm 🎉</button>
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1"><StickyNote size={11} /> Follow-up notes</p>
            {quickRemarks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="quick-remarks-row">
                {quickRemarks.map((q, i) => (
                  <button key={i} data-testid={`use-quick-remark-${i}`} onClick={() => addQuickRemark(q)}
                    title="Tap to log this remark"
                    className="flex items-center gap-1 bg-violet-50 border border-violet-300 text-violet-800 rounded-full px-2.5 py-1 text-[11px] font-semibold hover:bg-violet-600 hover:text-white transition-colors">
                    <Zap size={10} /> {q}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 mb-2">
              <input data-testid="followup-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addFollowup()} placeholder="Add a follow-up note…"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-yellow-500" />
              <button data-testid="followup-note-save" onClick={addFollowup} className="bg-yellow-400 text-yellow-950 rounded-lg px-3 py-1.5 text-xs font-bold hover:bg-yellow-500">Add</button>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto scroll-thin">
              {[...(lead.followups || [])].reverse().map((f, i) => (
                <div key={i} className="bg-yellow-50 border border-yellow-200 rounded px-2.5 py-1.5">
                  <p className="text-xs text-slate-800">{f.note}</p>
                  <p className="text-[10px] text-slate-400">{f.by_name} · {timeAgo(f.at)}</p>
                </div>
              ))}
              {[...(lead.touchpoints || [])].reverse().map((t, i) => (
                <div key={`t${i}`} className="bg-sky-50 border border-sky-200 rounded px-2.5 py-1.5 flex items-center gap-2">
                  {t.channel === 'CALL' ? <Phone size={11} className="text-sky-600" /> : <MessageCircle size={11} className="text-emerald-600" />}
                  <p className="text-[11px] text-slate-700">{t.outcome?.replace('_', ' ')} {t.note && `· ${t.note}`} <span className="text-slate-400">· {timeAgo(t.at)}</span></p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
